import { useNavigate } from 'react-router-dom';
import { User, LogOut, Shield } from 'lucide-react';
import { logout } from '../../../services/authService';

interface UserMenuProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string;
  userInitials: string;
  isAdmin?: boolean;
}

/**
 * UserMenu - Modal de menu do usuário no estilo Claude
 *
 * Exibe opções:
 * - Perfil
 * - Administração (somente para admins)
 * - Sair
 */
const UserMenu = ({ isOpen, onClose, userName, userEmail, userInitials, isAdmin }: UserMenuProps) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
      />

      {/* Menu Modal - positioned above the user avatar */}
      <div
        className="
          fixed z-[61]
          bg-[#2a2a2a] border border-[#444444] rounded-xl
          shadow-2xl min-w-[220px]
          overflow-hidden
        "
        style={{
          bottom: '80px',
          left: '10px',
        }}
      >
        {/* User Info Header */}
        <div className="px-4 py-3 border-b border-[#333333]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F4CC04] flex items-center justify-center flex-shrink-0">
              <span className="text-[#1a1a1a] font-bold text-xs">{userInitials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {userName || 'Usuário'}
              </p>
              {userEmail && (
                <p className="text-[#666666] text-xs truncate">
                  {userEmail}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-1">
          <button
            onClick={() => handleNavigate('/profile')}
            className="
              w-full flex items-center gap-3 px-4 py-2.5
              text-[#CCCCCC] hover:bg-[#333333] hover:text-white
              transition-colors text-left
            "
          >
            <User className="w-4 h-4" />
            <span className="text-sm">Perfil</span>
          </button>

          {/* Admin option - only visible for admin users */}
          {isAdmin && (
            <button
              onClick={() => handleNavigate('/admin')}
              className="
                w-full flex items-center gap-3 px-4 py-2.5
                text-[#CCCCCC] hover:bg-[#333333] hover:text-white
                transition-colors text-left
              "
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm">Administração</span>
            </button>
          )}
        </div>

        {/* Separator */}
        <div className="border-t border-[#333333]" />

        {/* Logout */}
        <div className="py-1">
          <button
            onClick={handleLogout}
            className="
              w-full flex items-center gap-3 px-4 py-2.5
              text-[#CCCCCC] hover:bg-[#333333] hover:text-white
              transition-colors text-left
            "
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Sair</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default UserMenu;
